import { useEffect, useRef, useState, useCallback, RefObject } from 'react';
import { useVideoProgress } from './useVideoProgress';
import { useUpdateVideoProgress } from './useUpdateVideoProgress';
import { VideoProgressEntry } from '@usacsmideo/types';

interface UseVideoProgressTrackingParams {
  patientId: string;
  videoId: string;
  videoRef: RefObject<HTMLVideoElement | null>;
  autoPlay?: boolean;
}

interface UseVideoProgressTrackingReturn {
  fullyWatched: boolean;
  savedProgress: VideoProgressEntry | undefined;
  isReady: boolean;
}

// Constants for throttling and completion detection
const SAVE_THROTTLE_MS = 5000; // Minimum 5 seconds between saves
const MIN_POSITION_ADVANCE = 2; // Only save if position advanced by at least 2 seconds
const COMPLETION_SECONDS_FROM_END = 10; // Mark completed when within 10 seconds of end
const NEAR_END_SKIP_THRESHOLD = 5; // Don't seek if within 5 seconds of end

/**
 * Hook that integrates HTML5 video player events with video progress tracking.
 * Handles position restore, throttled saves, and completion detection.
 */
export function useVideoProgressTracking({
  patientId,
  videoId,
  videoRef,
  autoPlay = false,
}: UseVideoProgressTrackingParams): UseVideoProgressTrackingReturn {
  const { getProgress, isReady: chartIsReady } = useVideoProgress(patientId);
  const { mutate: saveProgress } = useUpdateVideoProgress();

  // Track completion state
  const [fullyWatched, setFullyWatched] = useState(false);
  const [hasRestoredPosition, setHasRestoredPosition] = useState(false);

  // Refs for tracking save throttling (don't trigger re-renders)
  const lastSavedPosition = useRef<number>(0);
  const lastSaveTime = useRef<number>(0);
  const hasInitialized = useRef<boolean>(false);

  // Get saved progress
  const savedProgress = getProgress(videoId);

  // Initialize completion state from saved progress
  useEffect(() => {
    if (savedProgress?.completed) {
      setFullyWatched(true);
    }
  }, [savedProgress?.completed]);

  // Save progress to Firestore with throttling
  const handleSaveProgress = useCallback(
    (position: number, duration: number, completed: boolean = false) => {
      // Don't save until initialized
      if (!hasInitialized.current) return;

      const now = Date.now();

      // Completion saves bypass throttle
      if (completed) {
        saveProgress({
          patientId,
          videoId,
          position,
          duration,
          completed: true,
        });
        lastSavedPosition.current = position;
        lastSaveTime.current = now;
        setFullyWatched(true);
        return;
      }

      // Check throttle timing
      if (now - lastSaveTime.current < SAVE_THROTTLE_MS) return;

      // Only save if position has advanced meaningfully (prevents overwriting restored position)
      if (position - lastSavedPosition.current < MIN_POSITION_ADVANCE) return;

      saveProgress({
        patientId,
        videoId,
        position,
        duration,
      });
      lastSavedPosition.current = position;
      lastSaveTime.current = now;
    },
    [patientId, videoId, saveProgress],
  );

  // Handle time update events from video
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || !hasInitialized.current) return;

    const position = video.currentTime;
    const duration = video.duration;

    if (!duration || isNaN(duration)) return;

    // Check if video is near completion (within X seconds of end)
    const secondsRemaining = duration - position;
    if (secondsRemaining <= COMPLETION_SECONDS_FROM_END && !fullyWatched) {
      handleSaveProgress(position, duration, true);
    } else {
      handleSaveProgress(position, duration);
    }
  }, [videoRef, fullyWatched, handleSaveProgress]);

  // Handle video ended event
  const handleEnded = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const duration = video.duration;
    if (duration && !isNaN(duration) && !fullyWatched) {
      handleSaveProgress(duration, duration, true);
    }
  }, [videoRef, fullyWatched, handleSaveProgress]);

  // Handle metadata loaded - restore position
  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video || !chartIsReady || hasRestoredPosition) return;

    const duration = video.duration;
    if (!duration || isNaN(duration)) return;

    // Mark as initialized
    hasInitialized.current = true;

    // Restore position if we have saved progress
    if (savedProgress && savedProgress.lastPosition > 0) {
      const nearEnd = duration - savedProgress.lastPosition < NEAR_END_SKIP_THRESHOLD;

      // Don't seek if video was completed or position is near end
      if (!savedProgress.completed && !nearEnd) {
        video.currentTime = savedProgress.lastPosition;
        lastSavedPosition.current = savedProgress.lastPosition;
      }
    }

    setHasRestoredPosition(true);

    // Auto-play if enabled and video not already completed
    if (autoPlay && !savedProgress?.completed) {
      // Use requestAnimationFrame to ensure player is ready
      requestAnimationFrame(() => {
        video.play().catch((error) => {
          // Ignore autoplay errors (browser may block)
          console.debug('Auto-play prevented:', error);
        });
      });
    }
  }, [videoRef, chartIsReady, hasRestoredPosition, savedProgress, autoPlay]);

  // Set up event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    // If metadata already loaded, trigger handler
    if (video.readyState >= 1) {
      handleLoadedMetadata();
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [videoRef, handleLoadedMetadata, handleTimeUpdate, handleEnded]);

  // Reset state when videoId changes
  useEffect(() => {
    setFullyWatched(savedProgress?.completed ?? false);
    setHasRestoredPosition(false);
    hasInitialized.current = false;
    lastSavedPosition.current = 0;
    lastSaveTime.current = 0;
  }, [videoId, savedProgress?.completed]);

  return {
    fullyWatched,
    savedProgress,
    isReady: chartIsReady && hasRestoredPosition,
  };
}
